import {
  Box,
  Checkbox,
  ClickAwayListener,
  InputAdornment,
  Typography,
} from "@mui/material";
import styles from "../ExpenseRequest.module.scss";
import { useAppSelector } from "../../../../app/hook";
import {
  CommonButton,
  CommonOutlinedButton,
  CustomRequiredInputLabel,
  CustomTextFeild,
} from "../../../common/StyledComponents/StyledFields";
import CustomSelectField from "../../../common/CustomSelectField/CustomSelectField";
import { useFormik } from "formik";
import { AttachMoney } from "@mui/icons-material";
import ButtonLoader from "../../../common/ButtonLoader/ButtonLoader";
import CustomDatePickerField from "../../../common/DatePicker/DatePicker";
import { useEffect, useState } from "react";
import {
  apiRequest,
  catchErrorMessageHandler,
  displayDateFormat,
  displayDateTimeFormat,
} from "../../../../infrastructure/backendService";
import apiPaths from "../../../../config/apiPaths.json";
import constants from "../../../../config/constants.json";
import toast from "react-hot-toast";
import SuccessPopup from "../../../devotee/donation/successPopup";
import ErrorPopup from "../../../common/ErrorPopup";
import AddVendorDialog from "./AddVendorDialog";
import UploadReceipt, { MAX_FILE_SIZE } from "./UploadReceipt";
import { number, object, string } from "yup";
import apiPath from "../../../../config/apiPaths.json";
import APILoading from "../../../common/APILoading/APILoading";

export const textFieldStyles = {
  textField: {
    border: "1px solid rgba(136, 46, 20, 0.4)",
    backgroundColor: "#FFF9E4",
    borderRadius: "8px",
  },
  errorTextField: {
    border: "1px solid #f05802",
    borderRadius: "8px",
    width: "100%",
    backgroundColor: "#FFF9E4",
  },
};

interface CreateExpenseRequestPorps {
  handleBack: () => void;
  updateExpenseRequest?: boolean;
  referenceNumber?: string;
  year?: any;
}

const CreateExpenseRequest = (props: CreateExpenseRequestPorps) => {
  const { handleBack, referenceNumber, updateExpenseRequest, year } = props;
  const {
    selectedExpenseCategory,
    selectedAccountNumber,
    expenseDetailsBudget,
  } = useAppSelector((state) => state.expenseRequest);

  const [vendorPageNumber, setVendorPageNumber] = useState(0);
  const [vendorsList, setVendorsList] = useState<any[]>([]);

  const [createBtnLoading, setCreateBtnLoading] = useState(false);
  const [retrievingRequestData, setRetrievingRequestData] = useState(false);
  const [expenseDataFetched, setExpenseDataFetched] = useState(false);
  const [openAddVendorDialog, setOpenAddVendorDialog] = useState(false);
  const [updateVendor, setupdateVendor] = useState(false);
  const [receiptsData, setReceiptsData] = useState<
    {
      amount: number;
      description: string;
      billOrgFileName: string;
      file: File;
      uploadedOn: any;
    }[]
  >([]);
  const [openUploadReceipt, setOpenUploadReceipt] = useState(false);
  const [fileSizeError, setFileSizeError] = useState("");

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<any[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);

  const initialValues = {
    year: year?.toString(),
    shortTitle: "",
    description: "",
    amountInDollars: "",
    devoteeSponsored: false,
    nonBudgetedItem: false,
    receiptsNotRequired: false,
    onBehalfOfCommitteeChair: false,
    vendorId: null,
    mailCheckOnOrBefore: "",
    dueUponReceipt: "",
  };

  const validationSchema = object().shape({
    shortTitle: string().trim().required("Short title is required"),
    amountInDollars: number()
      .required("Amount is required")
      .min(1, "Minimum amount must be $1"),
    description: string().trim().required("Description is required"),
    vendorId: string().trim().required("Please select the vendor"),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit(values) {
      let totalFileSize = 0;
      receiptsData?.forEach((fileData: any) => {
        totalFileSize += Number(fileData?.file?.size ?? 0);
      });
      if (totalFileSize <= MAX_FILE_SIZE * 10) {
        setFileSizeError("");
        if (updateExpenseRequest) {
          handleUpdateExpenseRequest();
        } else {
          handleSubmitExpenseRequest();
        }
      } else {
        setFileSizeError("The total size of files should not exceed 100MB");
      }
    },
    enableReinitialize: true,
  });

  const fetchVendorsList = () => {
    apiRequest({
      method: "GET",
      endpoint: apiPath.templeVendors,
      includeToken: true,
      params: {
        size: 500,
        sortBy: "vendorName",
        page: vendorPageNumber,
      },
    })
      .then((res) => {
        setVendorsList((prevState) => {
          let data = [...prevState, ...res?.data?.content];
          data?.sort((a, b) => a?.vendorName?.localeCompare(b?.vendorName));

          const uniqueItems: any[] = [];
          const seenVendorIds = new Set<number>();

          for (const vendor of data) {
            if (!seenVendorIds.has(vendor?.id)) {
              seenVendorIds.add(vendor?.id);
              uniqueItems.push(vendor);
            }
          }
          return uniqueItems;
        });
        setTimeout(() => {
          setVendorPageNumber((prevState) => {
            let nextPage = prevState + 1;
            if (nextPage >= res?.data?.totalPages) {
              return prevState;
            } else {
              return nextPage;
            }
          });
        }, 100);
      })
      .catch((err) => {
        console.log(err);
        toast.custom(
          <ErrorPopup showMessage={catchErrorMessageHandler(err)} />,
          {
            duration: constants.TOAST_POPUP_DURATION,
          }
        );
      });
  };

  const fetchExpenseRequestDetails = () => {
    if (referenceNumber) {
      setRetrievingRequestData(true);
      apiRequest({
        method: "GET",
        endpoint: `${apiPath.expenseDetailsWithVendor?.replace(
          "{referenceNumber}",
          referenceNumber
        )}`,
        includeToken: true,
      })
        .then((res) => {
          setExpenseDataFetched(true);
          const data = res?.data;
          formik.setFieldValue("shortTitle", data?.shortTitle);
          formik.setFieldValue("description", data?.description);
          formik.setFieldValue("amountInDollars", data?.amountInDollars);
          formik.setFieldValue("devoteeSponsored", data?.devoteeSponsored);
          formik.setFieldValue("nonBudgetedItem", data?.nonBudgetedItem);
          formik.setFieldValue(
            "receiptsNotRequired",
            data?.receiptsNotRequired
          );
          formik.setFieldValue(
            "onBehalfOfCommitteeChair",
            data?.onBehalfOfCommitteeChair
          );
          formik.setFieldValue("vendorId", data?.vendorDetails?.id ?? "");
          formik.setFieldValue(
            "mailCheckOnOrBefore",
            data?.mailCheckOnOrBefore
          );
          formik.setFieldValue("dueUponReceipt", data?.dueUponReceipt);
          setQuery(data?.vendorDetails?.vendorName ?? "");
          setVendorsList((prevState: any) => {
            let vendorExists = prevState?.find(
              (vendor: any) => vendor?.id === data?.vendorDetails?.id
            );
            if (!vendorExists) {
              return [...prevState, data?.vendorDetails];
            } else {
              return prevState;
            }
          });
          setReceiptsData([
            ...data?.expenseBills?.map((receipt: any) => ({
              ...receipt,
              uploadedOn: receipt?.enteredOn,
            })),
          ]);
          setRetrievingRequestData(false);
        })
        .catch((err) => {
          console.log(err);
          setExpenseDataFetched(true);
          setRetrievingRequestData(false);
          toast.custom(
            <ErrorPopup showMessage={catchErrorMessageHandler(err)} />,
            {
              duration: constants.TOAST_POPUP_DURATION,
            }
          );
        });
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 300, behavior: "smooth" });
    if (updateExpenseRequest && referenceNumber) {
      fetchExpenseRequestDetails();
    }
  }, [updateExpenseRequest, referenceNumber]);

  useEffect(() => {
    if (!updateExpenseRequest || (updateExpenseRequest && expenseDataFetched)) {
      fetchVendorsList();
    }
  }, [vendorPageNumber, expenseDataFetched]);

  const handleSearchVendor = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    if (value.length > 0) {
      const filteredResults = vendorsList?.filter((item) =>
        item?.vendorName?.toLowerCase().includes(value?.toLowerCase())
      );
      setResults(filteredResults);
      setShowDropdown(true);
    } else {
      setResults([]);
      setShowDropdown(false);
    }
  };

  const handleSelect = (value: any) => {
    setQuery(value?.vendorName);
    setShowDropdown(false);
    formik.setFieldValue("vendorId", value?.id);
  };

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (query.length > 0) {
      setShowDropdown(true);
      handleSearchVendor(e);
    }
  };

  const handleClickAway = () => {
    setShowDropdown(false);
  };

  const handleSelectNewVendor = (newVendor: any) => {
    setVendorsList((prevState) => [...prevState, newVendor]);
    formik.setFieldValue("vendorId", newVendor?.id);
    formik.setFieldError("vendorId", "");
    setQuery(newVendor?.vendorName);
  };

  const handleSetUpdatedVendor = (updatedVendor: any) => {
    setVendorsList((prevState) =>
      prevState?.map((vendor: any) => {
        if (vendor?.id === updatedVendor?.id) {
          return updatedVendor;
        } else {
          return vendor;
        }
      })
    );
    formik.setFieldValue("vendorId", updatedVendor?.id);
    formik.setFieldError("vendorId", "");
    setQuery(updatedVendor?.vendorName);
  };

  const handleDateChange = (field: string, value: any) => {
    formik.setFieldValue(field, value);
  };

  const handleSubmitExpenseRequest = () => {
    const { values } = formik;
    const payload = {
      ...values,
      mainAccountName: selectedExpenseCategory?.accountId,
      mainAccountBudget: expenseDetailsBudget?.mainAccountBudgetAmount,
      subAccountName: selectedAccountNumber?.accountId,
      subAccountBudget: expenseDetailsBudget?.subAccountBudgetAmount,
      totalApprovedExpenses: expenseDetailsBudget?.totalApprovedExpenses,
      totalExpensesRequested: expenseDetailsBudget?.totalExpensesSpentToDate,
      balance: expenseDetailsBudget?.balance,
      mailCheckOnOrBefore: values?.mailCheckOnOrBefore
        ? displayDateFormat(new Date(values?.mailCheckOnOrBefore))
        : "",
      dueUponReceipt: values?.dueUponReceipt
        ? displayDateFormat(new Date(values?.dueUponReceipt))
        : "",
      amountInDollars: values?.amountInDollars,
      bills: [
        ...receiptsData?.map((receipt: any) => ({
          description: receipt?.description,
          amount: parseFloat(receipt?.amount),
          billOrgFileName: receipt?.billOrgFileName,
        })),
      ],
    };

    const formData = new FormData();

    // Add text data to the form using blob
    const reqData = JSON.stringify(payload);
    formData.append(
      "expenseDetails",
      new Blob([reqData], { type: "application/json" })
    );

    // Append multiple files individually
    receiptsData?.forEach((receipt) => {
      formData.append("files", receipt?.file); // Adding each file individually
    });

    setCreateBtnLoading(true);
    apiRequest({
      method: "POST",
      endpoint: apiPaths.createExpenseDetails,
      includeToken: true,
      body: formData,
      headers: {},
    })
      .then((res) => {
        toast.custom(
          <SuccessPopup
            headerMessage="New Expense Request"
            showMessage={`Your reference number is: ${res?.data?.referenceNumber}`}
            description="Please write this reference number on receipt"
            isDurationDisabled={true}
          />
        );
        formik.resetForm();
        setReceiptsData([]);
        setCreateBtnLoading(false);
        handleBack();
      })
      .catch((err) => {
        setCreateBtnLoading(false);
        toast.custom(
          <ErrorPopup showMessage={catchErrorMessageHandler(err)} />,
          {
            duration: constants.TOAST_POPUP_DURATION,
          }
        );
      });
  };

  const handleUpdateExpenseRequest = () => {
    const { values } = formik;
    const payload = {
      ...values,
      year: new Date()?.getFullYear(),
      mainAccountName: selectedExpenseCategory?.accountId,
      mainAccountBudget: expenseDetailsBudget?.mainAccountBudgetAmount,
      subAccountName: selectedAccountNumber?.accountId,
      subAccountBudget: expenseDetailsBudget?.subAccountBudgetAmount,
      totalApprovedExpenses: expenseDetailsBudget?.totalApprovedExpenses,
      totalExpensesRequested: expenseDetailsBudget?.totalExpensesSpentToDate,
      balance: expenseDetailsBudget?.balance,
      mailCheckOnOrBefore: values?.mailCheckOnOrBefore
        ? displayDateFormat(new Date(values?.mailCheckOnOrBefore))
        : "",
      dueUponReceipt: values?.dueUponReceipt
        ? displayDateFormat(new Date(values?.dueUponReceipt))
        : "",
      amountInDollars: values?.amountInDollars,
      bills: [
        ...receiptsData?.map((receipt: any) => ({
          description: receipt?.description,
          amount: parseFloat(receipt?.amount),
          billOrgFileName: receipt?.billOrgFileName,
          id: receipt?.id,
          referenceNumber,
          billFileName: receipt?.billFileName,
        })),
      ],
    };

    const formData = new FormData();

    // Add text data to the form using blob
    const reqData = JSON.stringify(payload);
    formData.append(
      "expenseDetails",
      new Blob([reqData], { type: "application/json" })
    );

    // Append multiple files individually
    receiptsData?.forEach((receipt) => {
      if (receipt?.file) {
        formData.append("files", receipt?.file); // Adding each file individually
      }
    });

    setCreateBtnLoading(true);
    apiRequest({
      method: "PUT",
      endpoint: `${apiPaths.createExpenseDetails}/${referenceNumber}`,
      includeToken: true,
      body: formData,
      headers: {},
    })
      .then((res) => {
        toast.custom(
          <SuccessPopup
            headerMessage="Update Expense Request"
            showMessage={`Updated the Expense Request: ${res?.data?.referenceNumber}`}
            isDurationDisabled={true}
          />
        );
        formik.resetForm();
        setReceiptsData([]);
        setCreateBtnLoading(false);
        handleBack();
      })
      .catch((err) => {
        setCreateBtnLoading(false);
        toast.custom(
          <ErrorPopup showMessage={catchErrorMessageHandler(err)} />,
          {
            duration: constants.TOAST_POPUP_DURATION,
          }
        );
      });
  };

  const handleUploadReceipt = (data: any) => {
    setReceiptsData([
      ...receiptsData,
      {
        billOrgFileName: data?.fileName,
        amount: data?.amount,
        description: data?.description,
        file: data?.file,
        uploadedOn: displayDateTimeFormat(new Date()),
      },
    ]);
    setOpenUploadReceipt(false);
  };

  const handleDeleteReceipt = (index: number) => {
    const uploadedReceipts = [...receiptsData];
    uploadedReceipts.splice(index, 1);
    setReceiptsData(uploadedReceipts);
    setFileSizeError("");
  };

  const getVendorDetails = () => {
    const selectedVendorDetails = vendorsList?.find(
      (vendor: any) => vendor?.id === formik.values.vendorId
    );

    if (!selectedVendorDetails) {
      return { name: undefined, address: "" };
    }

    const { vendorName, address, city, state, zipcode } = selectedVendorDetails;

    // Build the address using an array, then join with commas as needed.
    const formattedAddress = [address, city, state]
      ?.filter(Boolean)
      ?.join(", ");

    const fullAddress = zipcode
      ? `${formattedAddress} - ${zipcode}`
      : formattedAddress; // Add dash and zipcode if present

    return {
      name: vendorName,
      address: fullAddress,
    };
  };

  type checkBoxFieldTypes =
    | "devoteeSponsored"
    | "receiptsNotRequired"
    | "onBehalfOfCommitteeChair"
    | "nonBudgetedItem";

  const renderCheckBoxContainer = (
    field: checkBoxFieldTypes,
    label: string,
    required: false
  ) => {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mr: "50px",
        }}
      >
        <Checkbox
          checked={formik.values?.[field]}
          onChange={formik.handleChange}
          name={field}
          sx={{ mr: "5px", p: "0px", color: "#F05802 !important" }}
        />
        {CustomRequiredInputLabel(label, required)}
      </Box>
    );
  };

  const renderMainAccountDetails = () => {
    return (
      <Box>
        <Typography
          className="sub_heading"
          sx={{ fontSize: "16px !important" }}
        >
          Main Account
        </Typography>
        <Box className="common_box_shadow" sx={{ mt: "10px" }}>
          <Box sx={{ display: { xs: "block", sm: "flex" } }}>
            <Box
              sx={{
                width: { xs: "100%", sm: "50%" },
                maxWidth: "350px",
                mr: { xs: "0px", sm: "20px" },
              }}
            >
              {CustomRequiredInputLabel("Committee", true)}
              <CustomTextFeild
                fullWidth
                required
                value={`${selectedExpenseCategory?.description} (${selectedExpenseCategory?.accountId})`}
                sx={textFieldStyles.textField}
                disabled
              />
            </Box>
            <Box sx={{ width: { xs: "100%", sm: "50%" }, maxWidth: "350px" }}>
              {CustomRequiredInputLabel("Budget", true)}
              <CustomTextFeild
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoney
                        fontSize="small"
                        sx={{ position: "absolute", opacity: 0.5 }}
                      />
                    </InputAdornment>
                  ),
                }}
                value={expenseDetailsBudget?.mainAccountBudgetAmount?.toFixed(
                  2
                )}
                sx={textFieldStyles.textField}
                disabled
              />
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderSubAccountDetails = () => {
    return (
      <Box mt="20px">
        <Typography
          className="sub_heading"
          sx={{ fontSize: "16px !important" }}
        >
          Sub Account
        </Typography>
        <Box className="common_box_shadow" sx={{ mt: "10px" }}>
          <Box sx={{ display: { xs: "block", sm: "flex" } }}>
            <Box
              sx={{
                width: { xs: "100%", sm: "50%" },
                maxWidth: "350px",
                mr: { xs: "0px", sm: "20px" },
              }}
            >
              {CustomRequiredInputLabel("Account Name", true)}
              <CustomTextFeild
                fullWidth
                required
                value={`${selectedAccountNumber?.description} (${selectedAccountNumber?.accountId})`}
                sx={textFieldStyles.textField}
                disabled
              />
            </Box>
            <Box sx={{ width: { xs: "100%", sm: "50%" }, maxWidth: "350px" }}>
              {CustomRequiredInputLabel("Budget", true)}
              <CustomTextFeild
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoney
                        fontSize="small"
                        sx={{ position: "absolute", opacity: 0.5 }}
                      />
                    </InputAdornment>
                  ),
                }}
                value={expenseDetailsBudget?.subAccountBudgetAmount?.toFixed(2)}
                sx={textFieldStyles.textField}
                disabled
              />
            </Box>
          </Box>
          <Box sx={{ mt: "5px", display: { xs: "block", sm: "flex" } }}>
            <Box
              sx={{
                width: { xs: "100%", sm: "50%" },
                maxWidth: "350px",
                mr: { xs: "0px", sm: "20px" },
              }}
            >
              {CustomRequiredInputLabel("Total Approved Expenses", true)}
              <CustomTextFeild
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoney
                        fontSize="small"
                        sx={{ position: "absolute", opacity: 0.5 }}
                      />
                    </InputAdornment>
                  ),
                }}
                value={expenseDetailsBudget?.totalApprovedExpenses?.toFixed(2)}
                sx={textFieldStyles.textField}
                disabled
              />
            </Box>
            <Box sx={{ width: { xs: "100%", sm: "50%" }, maxWidth: "350px" }}>
              {CustomRequiredInputLabel("Total Expenses Spent to date", true)}
              <CustomTextFeild
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoney
                        fontSize="small"
                        sx={{ position: "absolute", opacity: 0.5 }}
                      />
                    </InputAdornment>
                  ),
                }}
                value={expenseDetailsBudget?.totalExpensesSpentToDate?.toFixed(
                  2
                )}
                sx={textFieldStyles.textField}
                disabled
              />
            </Box>
          </Box>
          <Box sx={{ mt: "5px", display: "flex" }}>
            <Box sx={{ width: { xs: "100%", sm: "50%" }, maxWidth: "350px" }}>
              {CustomRequiredInputLabel("Balance", true)}
              <CustomTextFeild
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoney
                        fontSize="small"
                        sx={{ position: "absolute", opacity: 0.5 }}
                      />
                    </InputAdornment>
                  ),
                }}
                value={`(${Math.abs(expenseDetailsBudget?.balance)?.toFixed(
                  2
                )})`}
                sx={textFieldStyles.textField}
                disabled
              />
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderNewExpenseDetails = () => {
    return (
      <Box sx={{ mt: "20px" }}>
        <Typography
          className="sub_heading"
          sx={{ fontSize: "16px !important" }}
        >
          New Expense Request
        </Typography>
        <Box className="common_box_shadow" sx={{ mt: "10px" }}>
          <Box sx={{ display: { xs: "block", sm: "flex" } }}>
            <Box
              sx={{
                width: { xs: "100%", sm: "50%" },
                maxWidth: "350px",
                mr: { xs: "0px", sm: "20px" },
              }}
            >
              {CustomRequiredInputLabel("Short Title", true)}
              <CustomTextFeild
                fullWidth
                required
                value={formik.values.shortTitle}
                sx={textFieldStyles.textField}
                onChange={formik.handleChange}
                name="shortTitle"
                placeholder="Short title"
              />
              {formik.touched.shortTitle && formik?.errors?.shortTitle && (
                <Typography sx={{ color: "red", fontSize: "12px" }}>
                  {formik?.errors?.shortTitle}
                </Typography>
              )}
            </Box>
            <Box sx={{ width: { xs: "100%", sm: "50%" }, maxWidth: "350px" }}>
              {CustomRequiredInputLabel("Amount in Dollars", true)}
              <CustomTextFeild
                fullWidth
                required
                name="amountInDollars"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <AttachMoney
                        fontSize="small"
                        sx={{ position: "absolute" }}
                      />
                    </InputAdornment>
                  ),
                }}
                value={formik.values.amountInDollars}
                placeholder="Enter amount"
                onChange={(e) => {
                  const numericValue = e.target.value
                    .replace(/[^\d.]/g, "")
                    .slice(0, 15); // Allow only the first 10 numbers
                  formik.setFieldValue("amountInDollars", numericValue);
                }}
                sx={
                  formik.touched.amountInDollars &&
                  formik.errors.amountInDollars
                    ? textFieldStyles.errorTextField
                    : textFieldStyles.textField
                }
              />
              {formik.touched.amountInDollars &&
                formik?.errors?.amountInDollars && (
                  <Typography sx={{ color: "red", fontSize: "12px" }}>
                    {formik?.errors?.amountInDollars}
                  </Typography>
                )}
            </Box>
          </Box>
          <Box sx={{ mt: "5px", width: "100%", maxWidth: "721px" }}>
            {CustomRequiredInputLabel("Description", true)}
            <textarea
              name="description"
              className={styles.text_area_field}
              value={formik.values.description}
              onChange={formik.handleChange}
            />
            {formik.touched.description && formik?.errors?.description && (
              <Typography sx={{ color: "red", fontSize: "12px" }}>
                {formik?.errors?.description}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              mt: "5px",
              rowGap: { xs: "10px", sm: "0px" },
            }}
          >
            {renderCheckBoxContainer(
              "devoteeSponsored",
              "Devotee Sponsored?",
              false
            )}
            {renderCheckBoxContainer(
              "nonBudgetedItem",
              "Non-budgeted Item?",
              false
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  const renderVendorSearchComponent = () => {
    return (
      <ClickAwayListener onClickAway={handleClickAway}>
        <Box className={styles.vendor_search_component}>
          <CustomTextFeild
            fullWidth
            required
            value={query}
            sx={textFieldStyles.textField}
            onChange={handleSearchVendor}
            name="searchVendor"
            placeholder="Enter vendor name..."
            onFocus={handleInputFocus}
            autoComplete="off"
          />
          {formik.touched.vendorId && formik?.errors?.vendorId && (
            <Typography sx={{ color: "red", fontSize: "12px" }}>
              {formik?.errors?.vendorId}
            </Typography>
          )}
          <Box>
            {showDropdown && results.length > 0 && (
              <Box id="dropdown" className={styles.dropdown_styles}>
                {results.map((result) => (
                  <div
                    key={result.id}
                    className={styles.item_styles}
                    onClick={() => handleSelect(result)}
                  >
                    {result?.vendorName}
                  </div>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </ClickAwayListener>
    );
  };

  const renderSelectVenderComponent = () => {
    return (
      <Box sx={{ mt: "20px" }}>
        <Typography
          className="sub_heading"
          sx={{ fontSize: "16px !important", mt: "0px !important" }}
        >
          Select Vendor
        </Typography>
        <Box className="common_box_shadow" sx={{ mt: "10px" }}>
          <Box>
            {CustomRequiredInputLabel("Search Vendor", true)}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: { xs: "flex-start", md: "center" },
              }}
            >
              {renderVendorSearchComponent()}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                <CommonButton
                  onClick={() => {
                    setOpenAddVendorDialog(true);
                    setupdateVendor(true);
                  }}
                  disabled={!formik.values.vendorId}
                  sx={{
                    ml: { xs: "0px", md: "10px" },
                    mt: { xs: "10px", md: "0px" },
                  }}
                >
                  <ButtonLoader
                    isBtnLoading={false}
                    btnText={"Update Vendor"}
                  />
                </CommonButton>
                <CommonButton
                  onClick={() => {
                    setOpenAddVendorDialog(true);
                  }}
                  sx={{
                    // ml: { xs: "10px", sm: "10px" },
                    mt: { xs: "10px", md: "0px" },
                  }}
                >
                  <ButtonLoader isBtnLoading={false} btnText={"Add Vendor"} />
                </CommonButton>
              </Box>
            </Box>
            <Box sx={{ my: "15px" }}>
              <Typography className={styles.account_value_txt}>
                <Box component={"span"} className={styles.account_header_txt}>
                  Vendor Name:
                </Box>{" "}
                {getVendorDetails()?.name || "-"}
              </Typography>
              <Typography className={styles.account_value_txt}>
                <Box component={"span"} className={styles.account_header_txt}>
                  Address:
                </Box>{" "}
                {getVendorDetails()?.address || "-"}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: { xs: "block", sm: "flex" }, mt: "5px" }}>
            <Box
              sx={{
                width: { xs: "100%", sm: "50%" },
                maxWidth: "350px",
                mr: { xs: "0px", sm: "20px" },
              }}
            >
              {CustomRequiredInputLabel("Mail check on or before", false)}
              <CustomDatePickerField
                date={formik.values.mailCheckOnOrBefore}
                handleDateChange={handleDateChange}
                errorMessage={formik.errors.mailCheckOnOrBefore}
                name="mailCheckOnOrBefore"
              />
            </Box>
            <Box sx={{ width: { xs: "100%", sm: "50%" }, maxWidth: "350px" }}>
              {CustomRequiredInputLabel("Due upon receipt?", false)}
              <CustomDatePickerField
                date={formik.values.dueUponReceipt}
                handleDateChange={handleDateChange}
                errorMessage={formik.errors.dueUponReceipt}
                name="dueUponReceipt"
              />
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderUploadReceipts = () => {
    return (
      <Box sx={{ mt: "20px" }}>
        <Typography
          className="sub_heading"
          sx={{ fontSize: "16px !important" }}
        >
          Upload Receipts
        </Typography>
        <Box className="common_box_shadow" sx={{ mt: "10px" }}>
          <Box
            sx={{ mt: "5px", width: "100%", overflowX: "auto" }}
            id="expense_details"
          >
            <table className={styles.table_container}>
              <thead>
                <th>File Name</th>
                <th>Amount</th>
                <th>Description</th>
                <th>Uploaded on</th>
                <th>Action</th>
              </thead>
              <tbody>
                {receiptsData?.length > 0 ? (
                  receiptsData?.map?.((row: any, index: number) => (
                    <tr key={index}>
                      <td>{row?.billOrgFileName}</td>
                      <td className={styles.amount_text} width={"48px"}>
                        ${row?.amount}
                      </td>
                      <td className={styles.table_desc_txt}>
                        {row?.description}
                      </td>
                      <td>{row?.uploadedOn}</td>
                      <td>
                        <Typography onClick={() => handleDeleteReceipt(index)}>
                          Delete
                        </Typography>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} style={{ height: "20px" }}></td>
                  </tr>
                )}
              </tbody>
            </table>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "flex-start", sm: "center" },
              mt: "15px",
            }}
          >
            <CommonButton
              onClick={() => {
                setOpenUploadReceipt(true);
              }}
              sx={{ mr: "10px" }}
            >
              <ButtonLoader isBtnLoading={false} btnText={"Attach File"} />
            </CommonButton>
            <Typography
              className={styles.account_value_txt}
              sx={{ mt: { xs: "5px", sm: "0px" } }}
            >
              *All requests for reimbursement must be submitted only through the
              respective Functional Committee Chairperson or President
            </Typography>
          </Box>
          <Typography sx={{ color: "red", fontSize: "12px", mt: "10px" }}>
            {fileSizeError}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            mt: "15px",
            rowGap: { xs: "10px", sm: "0px" },
          }}
        >
          {renderCheckBoxContainer(
            "onBehalfOfCommitteeChair",
            "On behalf of Committee Chair?",
            false
          )}
          {renderCheckBoxContainer(
            "receiptsNotRequired",
            "Receipts not required",
            false
          )}
        </Box>
      </Box>
    );
  };

  const renderButtonsComponent = () => {
    return (
      <Box mt="20px">
        <CommonButton
          onClick={() => {
            !createBtnLoading && formik.handleSubmit();
          }}
          sx={{ mr: "10px" }}
        >
          <ButtonLoader
            isBtnLoading={createBtnLoading}
            btnText={`${updateExpenseRequest ? "Update" : "Submit"}`}
          />
        </CommonButton>
        <CommonOutlinedButton onClick={handleBack} sx={{ mr: "10px" }}>
          <ButtonLoader isBtnLoading={false} btnText={"Cancel"} />
        </CommonOutlinedButton>
      </Box>
    );
  };

  const render = () => {
    return (
      <Box className={styles.create_new_request_main}>
        <Typography className="sub_heading">New Expense Voucher</Typography>
        <Typography className={styles.authorization_txt}>
          Expense Authorisation Voucher
        </Typography>
        {renderMainAccountDetails()}
        {renderSubAccountDetails()}
        {renderNewExpenseDetails()}
        {renderSelectVenderComponent()}
        {renderUploadReceipts()}
        {renderButtonsComponent()}
        {openAddVendorDialog && (
          <AddVendorDialog
            open={openAddVendorDialog}
            handleClose={() => {
              setOpenAddVendorDialog(false);
              setupdateVendor(false);
            }}
            handleSelectNewVendor={handleSelectNewVendor}
            handleSetUpdatedVendor={handleSetUpdatedVendor}
            updateVendor={updateVendor}
            vendorId={formik.values.vendorId}
          />
        )}
        <UploadReceipt
          open={openUploadReceipt}
          handleClose={() => {
            setOpenUploadReceipt(false);
          }}
          handleUploadReceipt={handleUploadReceipt}
        />
      </Box>
    );
  };

  return <>{retrievingRequestData ? <APILoading /> : <>{render()}</>}</>;
};

export default CreateExpenseRequest;
